import { useEffect, useState } from "react";
import { Copy, ExternalLink, Link2, Loader2, Mail, Megaphone, Pencil, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtDateMtl } from "../../lib/dateMtl";
import ExpandableText from "./ExpandableText";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const CATEGORIES = {
  annuaire: "Annuaire",
  federation: "Fédération / organisme",
  partenaire: "Partenaire (badge)",
  presse: "Presse / médias",
  autre: "Autre",
};
const STATUTS = {
  a_faire: "À faire",
  soumis: "Soumis / en attente",
  obtenu: "Lien obtenu",
  refuse: "Refusé",
};
const PRIORITES = { haute: "Haute", moyenne: "Moyenne", basse: "Basse" };
const PRIO_STYLE = {
  haute: "border-gold/50 text-gold",
  moyenne: "border-ivory/25 text-ivory/70",
  basse: "border-ivory/15 text-ivory/50",
};

// Prospection de backlinks : liste des cibles (annuaires, fédérations, presse,
// clients-partenaires), suivi d'avancement et gabarits de courriels de demande.
// Un backlink = un site externe qui pointe vers mrtrophee.ca (notoriété Google/Bing/IA).
export default function BacklinksTab() {
  const [targets, setTargets] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("tous");
  // Ajout manuel d'une cible
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", email: "", category: "annuaire", priority: "moyenne", notes: "" });
  const [addBusy, setAddBusy] = useState(false);
  // Modification d'une cible
  const [editTarget, setEditTarget] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  // Gabarit de courriel de demande (badge / annuaire / partenariat)
  const [outOpen, setOutOpen] = useState(false);
  const [outKind, setOutKind] = useState("badge");
  const [outTarget, setOutTarget] = useState(null); // cible liée (ouvert depuis une ligne)
  const [outName, setOutName] = useState("");
  const [outEmail, setOutEmail] = useState("");     // courriel du destinataire (envoi direct)
  const [outSubject, setOutSubject] = useState("");
  const [outBody, setOutBody] = useState("");
  const [outBusy, setOutBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  // Import de clubs avec site web (prospects) comme cibles « partenaire » —
  // demande du propriétaire : proposer le badge à chaque club, par lots de 25
  const importClubs = async () => {
    setImportBusy(true);
    try {
      const { data } = await api.post("/admin/backlinks/import-badge-clubs", { limit: 25 });
      if (data.imported > 0) {
        toast.success(`${data.imported} clubs ajoutés (catégorie Partenaire) — ${data.remaining} autres disponibles.`);
        load();
      } else {
        toast.info("Tous les clubs avec site web sont déjà importés.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import impossible");
    } finally {
      setImportBusy(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/backlinks");
      setTargets(data.targets);
      setCounts(data.counts || {});
      setTotal(data.total || 0);
    } catch { /* silencieux */ } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = async (t0, fields, okMsg) => {
    try {
      await api.patch(`/admin/backlinks/${t0.id}`, fields);
      setTargets((prev) => prev.map((x) => (x.id === t0.id ? { ...x, ...fields } : x)));
      if (fields.status) {
        setCounts((c) => ({ ...c, [t0.status]: (c[t0.status] || 1) - 1, [fields.status]: (c[fields.status] || 0) + 1 }));
        if (okMsg) toast.success(okMsg);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Modification impossible");
      load();
    }
  };

  const addTarget = async () => {
    if (!form.name.trim()) return toast.error("Le nom de la cible est requis");
    setAddBusy(true);
    try {
      await api.post("/admin/backlinks", form);
      toast.success(`« ${form.name.trim()} » ajouté à la liste`);
      setForm({ name: "", url: "", email: "", category: "annuaire", priority: "moyenne", notes: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Ajout impossible");
    } finally {
      setAddBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      await api.patch(`/admin/backlinks/${editTarget.id}`, {
        name: editTarget.name, url: editTarget.url, email: editTarget.email || "",
        category: editTarget.category, priority: editTarget.priority, notes: editTarget.notes,
      });
      toast.success("Cible enregistrée");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Enregistrement impossible");
    } finally {
      setEditBusy(false);
    }
  };

  const remove = async (t0) => {
    if (!window.confirm(`Supprimer « ${t0.name} » de la liste ?`)) return;
    try {
      await api.delete(`/admin/backlinks/${t0.id}`);
      setTargets((prev) => prev.filter((x) => x.id !== t0.id));
      setTotal((n) => n - 1);
      toast.success("Cible supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  // Relance polie en un clic (demande du propriétaire) : cibles « Soumis » dont le
  // dernier envoi remonte à 7 jours ou plus.
  const [followBusy, setFollowBusy] = useState("");
  const joursDepuis = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 999);
  const relancable = (t0) => t0.status === "soumis" && (t0.last_outreach || t0.submitted_at)
    && joursDepuis(t0.last_outreach?.at || t0.submitted_at) >= 7;
  const relancer = async (t0) => {
    const to = t0.last_outreach?.to || t0.email;
    if (!window.confirm(`Envoyer une relance polie à ${to} pour « ${t0.name} » ?`)) return;
    setFollowBusy(t0.id);
    try {
      const { data } = await api.post(`/admin/backlinks/${t0.id}/send-followup`);
      toast.success(`Relance envoyée à ${data.to} — copie conservée à l'atelier.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Relance impossible");
    } finally {
      setFollowBusy("");
    }
  };

  // Ouvre le dialogue de courriel — l'effet ci-dessous charge le gabarit (une seule requête)
  const openOutreach = (t0, kind) => {
    setOutTarget(t0 || null);
    setOutKind(kind);
    setOutName(t0?.name || "");
    setOutEmail(t0?.email || "");
    setOutOpen(true);
  };

  // ENVOI DIRECT du courriel depuis l'admin (demande du propriétaire) : part de
  // MrTrophee.ca (réponses à commande@trophee.ca), copie à l'atelier, et la cible
  // passe à « Soumis » toute seule.
  const sendOutreach = async () => {
    if (!outEmail.trim() || !outEmail.includes("@")) return toast.error("Inscrivez le courriel du destinataire");
    if (!outTarget) return toast.error("Envoi direct possible seulement depuis la fiche d'une cible");
    if (!window.confirm(`Envoyer le courriel à ${outEmail.trim()} maintenant ?`)) return;
    setSendBusy(true);
    try {
      const { data } = await api.post(`/admin/backlinks/${outTarget.id}/send-outreach`,
        { to: outEmail.trim(), subject: outSubject, body: outBody });
      toast.success(`Courriel envoyé à ${data.to} — copie conservée à l'atelier.`);
      setOutOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setSendBusy(false);
    }
  };

  // Chargement du gabarit serveur à l'ouverture du dialogue et à chaque changement
  // de type — {cible} est remplacé par le nom saisi (côté serveur)
  useEffect(() => {
    if (!outOpen) return;
    setOutBusy(true);
    api.get("/admin/backlinks/outreach-template", { params: { kind: outKind, target: outName } })
      .then((r) => { setOutSubject(r.data.subject); setOutBody(r.data.body); })
      .catch(() => toast.error("Gabarit indisponible"))
      .finally(() => setOutBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outOpen, outKind]);

  // Réécriture IA du courriel (Claude) — le texte reste modifiable ensuite
  const aiRewrite = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/backlinks/outreach-ai", { kind: outKind, target: outName });
      setOutSubject(data.subject);
      setOutBody(data.body);
      toast.success("Message rédigé par l'IA — relisez et ajustez avant l'envoi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rédaction IA indisponible");
    } finally {
      setAiBusy(false);
    }
  };

  const filtered = targets.filter((t0) => filterCat === "tous" || t0.category === filterCat);
  const catCounts = targets.reduce((acc, t0) => ({ ...acc, [t0.category]: (acc[t0.category] || 0) + 1 }), {});

  return (
    <div data-testid="backlinks-tab" className="space-y-8">
      <div className="bg-surface border border-ivory/10 rounded-xl p-6">
        <h2 className="font-serif text-2xl text-champagne mb-2 flex items-center gap-3">
          <Link2 size={22} className="text-gold" /> Prospection de backlinks
        </h2>
        <p className="text-sm text-ivory/60 mb-6">
          Un backlink est un lien d'un site externe vers mrtrophee.ca — plus vous en avez (annuaires,
          fédérations, sites de clubs clients), plus Google, Bing et les IA jugent le site fiable et le font remonter.
          Suivez ici chaque cible, de « À faire » à « Lien obtenu ».
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Cibles suivies", value: total, testid: "backlinks-total" },
            { label: "À faire", value: counts.a_faire || 0, testid: "backlinks-todo" },
            { label: "En cours", value: counts.soumis || 0, testid: "backlinks-progress" },
            { label: "Liens obtenus", value: counts.obtenu || 0, testid: "backlinks-won", accent: true },
          ].map((s) => (
            <div key={s.testid} className={`rounded-xl border p-5 ${s.accent ? "border-emerald-500/30 bg-emerald-500/5" : "border-ivory/10 bg-ink"}`}>
              <p className="text-[11px] uppercase tracking-widest text-ivory/60 mb-1.5">{s.label}</p>
              <p data-testid={s.testid} className={`font-serif text-3xl ${s.accent ? "text-emerald-400" : "text-champagne"}`}>{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="backlinks-filters">
        <button data-testid="backlinks-filter-tous" onClick={() => setFilterCat("tous")}
          className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${filterCat === "tous" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
          Tous ({total})
        </button>
        {Object.keys(CATEGORIES).filter((c) => (catCounts[c] || 0) > 0).map((c) => (
          <button key={c} data-testid={`backlinks-filter-${c}`} onClick={() => setFilterCat(c)}
            className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${filterCat === c ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
            {CATEGORIES[c]} ({catCounts[c]})
          </button>
        ))}
        <div className="flex-1" />
        <button data-testid="backlinks-import-clubs" onClick={importClubs} disabled={importBusy}
          title="Importe 25 clubs avec site web (prospection) comme cibles « Partenaire (badge) » — courriel de contact inclus pour l'envoi en 1 clic"
          className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-300 px-5 py-2 rounded-full text-xs hover:bg-emerald-500 hover:text-ink transition-colors duration-300 disabled:opacity-50">
          {importBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Importer des clubs (badge)
        </button>
        <button data-testid="backlinks-template-btn" onClick={() => openOutreach(null, "badge")}
          className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300">
          <Megaphone size={14} /> Gabarits de courriels
        </button>
        <button data-testid="backlinks-add-btn" onClick={() => setAddOpen((o) => !o)}
          className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300">
          <Plus size={14} /> Ajouter une cible
        </button>
      </div>

      {addOpen && (
        <div className="bg-surface border border-gold/25 rounded-xl p-6 space-y-4" data-testid="backlinks-add-form">
          <p className="text-xs uppercase tracking-widest text-gold">Nouvelle cible de backlink</p>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="backlinks-add-name" type="text" value={form.name} placeholder="Nom (ex. : Blogue sportif local)"
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="bg-ink border border-ivory/15 rounded-full px-5 py-2.5 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none" />
            <input data-testid="backlinks-add-url" type="text" value={form.url} placeholder="Adresse du site (optionnel)"
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              className="bg-ink border border-ivory/15 rounded-full px-5 py-2.5 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none" />
            <input data-testid="backlinks-add-email" type="email" value={form.email} placeholder="Courriel du contact (pour l'envoi direct)"
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="bg-ink border border-ivory/15 rounded-full px-5 py-2.5 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none" />
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="backlinks-add-category" className="bg-ink border-ivory/15 rounded-full text-ivory text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-ivory/15 text-ivory">
                {Object.entries(CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger data-testid="backlinks-add-priority" className="bg-ink border-ivory/15 rounded-full text-ivory text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-ivory/15 text-ivory">
                {Object.entries(PRIORITES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <textarea data-testid="backlinks-add-notes" rows={2} value={form.notes} placeholder="Notes (comment obtenir le lien, contact, etc.)"
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            className="w-full bg-ink border border-ivory/15 rounded-xl px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none" />
          <button data-testid="backlinks-add-submit" onClick={addTarget} disabled={addBusy}
            className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-7 py-2.5 rounded-full text-sm hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
            {addBusy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Ajouter
          </button>
        </div>
      )}

      <div className="bg-surface border border-ivory/10 rounded-xl overflow-hidden" data-testid="backlinks-table">
        <Table>
          <TableHeader>
            <TableRow className="border-ivory/10 hover:bg-transparent">
              <TableHead className="text-ivory/60">Cible</TableHead>
              <TableHead className="text-ivory/60">Catégorie</TableHead>
              <TableHead className="text-ivory/60">Priorité</TableHead>
              <TableHead className="text-ivory/60">Statut</TableHead>
              <TableHead className="text-ivory/60">Lien obtenu</TableHead>
              <TableHead className="text-ivory/60 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-ivory/10 hover:bg-transparent">
                <TableCell colSpan={6} className="text-center py-10 text-ivory/50">
                  <Loader2 size={18} className="animate-spin inline-block" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow className="border-ivory/10 hover:bg-transparent">
                <TableCell colSpan={6} className="text-center py-10 text-ivory/50" data-testid="backlinks-empty">
                  Aucune cible dans cette catégorie.
                </TableCell>
              </TableRow>
            ) : filtered.map((t0) => (
              <TableRow key={t0.id} className="border-ivory/10 hover:bg-ivory/[0.03]" data-testid={`backlink-row-${t0.id}`}>
                <TableCell className="text-ivory/90 text-sm font-medium max-w-64">
                  <span className="inline-flex items-center gap-1.5">
                    {t0.url ? (
                      <a href={t0.url} target="_blank" rel="noreferrer" data-testid={`backlink-url-${t0.id}`}
                        className="hover:text-gold transition-colors underline-offset-2 hover:underline inline-flex items-center gap-1">
                        {t0.name} <ExternalLink size={11} className="opacity-50" />
                      </a>
                    ) : t0.name}
                  </span>
                  {t0.notes && (
                    <ExpandableText text={t0.notes} testId={`backlink-notes-${t0.id}`} className="block text-ivory/45 text-xs font-normal mt-1" />
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-[11px] border border-ivory/20 text-ivory/70 rounded-full px-2.5 py-1 whitespace-nowrap"
                    data-testid={`backlink-cat-${t0.id}`}>{CATEGORIES[t0.category] || t0.category}</span>
                </TableCell>
                <TableCell>
                  <span className={`text-[11px] border rounded-full px-2.5 py-1 ${PRIO_STYLE[t0.priority] || PRIO_STYLE.moyenne}`}
                    data-testid={`backlink-prio-${t0.id}`}>{PRIORITES[t0.priority] || t0.priority}</span>
                </TableCell>
                <TableCell>
                  <Select value={t0.status} onValueChange={(v) => update(t0, { status: v },
                    v === "obtenu" ? "Bravo — backlink obtenu ! Pensez à noter l'adresse de la page." : null)}>
                    <SelectTrigger data-testid={`backlink-status-${t0.id}`}
                      className={`h-8 w-40 bg-ink border rounded-full text-xs ${t0.status === "obtenu" ? "border-emerald-500/50 text-emerald-400" : t0.status === "soumis" ? "border-amber-500/40 text-amber-300" : "border-ivory/15 text-ivory"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-ivory/15 text-ivory">
                      {Object.entries(STATUTS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {t0.obtained_at && t0.status === "obtenu" && (
                    <span className="block text-[10px] text-emerald-400/70 mt-1 pl-1" data-testid={`backlink-obtained-${t0.id}`}>
                      obtenu le {fmtDateMtl(t0.obtained_at)}
                    </span>
                  )}
                  {t0.last_outreach && (
                    <span className="block text-[10px] text-ivory/45 mt-1 pl-1" data-testid={`backlink-sent-${t0.id}`}>
                      courriel envoyé le {fmtDateMtl(t0.last_outreach.at)} à {t0.last_outreach.to}
                    </span>
                  )}
                  {t0.last_followup && (
                    <span className="block text-[10px] text-amber-300/70 mt-1 pl-1" data-testid={`backlink-followup-${t0.id}`}>
                      relancée le {fmtDateMtl(t0.last_followup.at)}{t0.followup_count > 1 ? ` (${t0.followup_count}×)` : ""}
                    </span>
                  )}
                  {relancable(t0) && (
                    <button onClick={() => relancer(t0)} disabled={followBusy === t0.id}
                      data-testid={`backlink-followup-btn-${t0.id}`}
                      title="Sans réponse depuis 7 jours ou plus — envoie un suivi poli au même contact"
                      className="mt-1.5 ml-1 inline-flex items-center gap-1 border border-amber-500/40 text-amber-300 rounded-full px-2.5 py-0.5 text-[10px] hover:bg-amber-500 hover:text-ink transition-colors disabled:opacity-50">
                      {followBusy === t0.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                      Relancer
                    </button>
                  )}
                </TableCell>
                <TableCell className="min-w-44">
                  {t0.status === "obtenu" ? (
                    <input type="text" value={t0.link_url || ""} placeholder="https://… (page du lien)"
                      data-testid={`backlink-link-${t0.id}`}
                      onChange={(e) => setTargets((prev) => prev.map((x) => (x.id === t0.id ? { ...x, link_url: e.target.value } : x)))}
                      onBlur={(e) => e.target.value !== (t0.link_url || "") && update(t0, { link_url: e.target.value })}
                      className="w-full bg-ink border border-emerald-500/25 rounded-full px-3 py-1.5 text-xs text-emerald-300 placeholder:text-ivory/30 focus:border-emerald-500/60 focus:outline-none" />
                  ) : <span className="text-ivory/30 text-xs">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <span className="inline-flex items-center gap-0.5">
                    <button data-testid={`backlink-write-${t0.id}`}
                      onClick={() => openOutreach(t0, t0.category === "partenaire" ? "badge" : t0.category === "annuaire" ? "annuaire" : "partenariat")}
                      className="p-2 text-ivory/40 hover:text-gold transition-colors" title="Préparer un courriel de demande">
                      <Mail size={15} />
                    </button>
                    <button data-testid={`backlink-edit-${t0.id}`} onClick={() => setEditTarget({ ...t0 })}
                      className="p-2 text-ivory/40 hover:text-gold transition-colors" title="Modifier la cible">
                      <Pencil size={15} />
                    </button>
                    <button data-testid={`backlink-delete-${t0.id}`} onClick={() => remove(t0)}
                      className="p-2 text-ivory/40 hover:text-red-400 transition-colors" title="Supprimer">
                      <Trash2 size={15} />
                    </button>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialogue : gabarit de courriel de demande de backlink */}
      <Dialog open={outOpen} onOpenChange={setOutOpen}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-2xl" data-testid="backlink-outreach-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <Megaphone className="w-5 h-5 text-gold" /> Courriel de demande de lien
            </DialogTitle>
            <DialogDescription className="text-ivory/60 text-xs">
              Envoyez directement d'ici (le message part de MrTrophee.ca, les réponses arrivent
              à commande@trophee.ca) — ou copiez-le pour l'envoyer vous-même.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Select value={outKind} onValueChange={setOutKind}>
                <SelectTrigger data-testid="outreach-kind" className="w-64 bg-ink border-ivory/15 rounded-full text-sm text-ivory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-ivory/15 text-ivory">
                  <SelectItem value="badge">Badge partenaire (client)</SelectItem>
                  <SelectItem value="annuaire">Inscription à un annuaire</SelectItem>
                  <SelectItem value="partenariat">Partenariat (fédération / organisme)</SelectItem>
                </SelectContent>
              </Select>
              <input type="text" value={outName} onChange={(e) => setOutName(e.target.value)}
                data-testid="outreach-target-name" placeholder="Nom du destinataire (optionnel)"
                className="flex-1 min-w-48 bg-ink border border-ivory/15 rounded-full px-4 py-2 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none" />
              <button data-testid="outreach-ai-btn" onClick={aiRewrite} disabled={aiBusy || outBusy}
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                Réécrire avec l'IA
              </button>
            </div>
            {outTarget && (
              <input type="email" value={outEmail} onChange={(e) => setOutEmail(e.target.value)}
                data-testid="outreach-to-email" placeholder="Courriel du destinataire (ex. : info@partenaire.ca)"
                className="w-full bg-ink border border-gold/30 rounded-full px-4 py-2 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none" />
            )}
            {outBusy ? (
              <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gold" /></div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <input type="text" value={outSubject} onChange={(e) => setOutSubject(e.target.value)}
                    data-testid="outreach-subject"
                    className="flex-1 bg-ink border border-ivory/15 rounded-full px-4 py-2 text-sm text-ivory focus:border-gold/60 focus:outline-none" />
                  <button data-testid="outreach-copy-subject" onClick={() => copyText(outSubject, "Objet copié")}
                    className="p-2 border border-ivory/20 rounded-full text-ivory/60 hover:text-gold hover:border-gold/50 transition-colors" title="Copier l'objet">
                    <Copy size={14} />
                  </button>
                </div>
                <textarea rows={12} value={outBody} onChange={(e) => setOutBody(e.target.value)}
                  data-testid="outreach-body"
                  className="w-full bg-ink border border-ivory/15 rounded-xl px-4 py-3 text-sm text-ivory focus:border-gold/60 focus:outline-none" />
                <div className="flex flex-wrap items-center gap-3">
                  {outTarget && (
                    <button data-testid="outreach-send" onClick={sendOutreach} disabled={sendBusy || outBusy}
                      className="inline-flex items-center gap-2 bg-emerald-500 text-ink font-bold px-5 py-2 rounded-full text-xs hover:bg-emerald-400 transition-all disabled:opacity-50">
                      {sendBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                      Envoyer maintenant
                    </button>
                  )}
                  <button data-testid="outreach-copy-body" onClick={() => copyText(outBody, "Message copié — collez-le dans votre courriel")}
                    className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all">
                    <Copy size={13} /> Copier le message
                  </button>
                  <button data-testid="outreach-copy-all" onClick={() => copyText(`Objet : ${outSubject}\n\n${outBody}`, "Objet + message copiés")}
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors">
                    <Copy size={13} /> Copier objet + message
                  </button>
                  {outTarget && outTarget.status === "a_faire" && (
                    <button data-testid="outreach-mark-submitted"
                      onClick={async () => { await update(outTarget, { status: "soumis" }); setOutTarget((t) => ({ ...t, status: "soumis" })); toast.success(`« ${outTarget.name} » marqué « Soumis / en attente »`); }}
                      className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-300 px-5 py-2 rounded-full text-xs hover:bg-amber-500 hover:text-ink transition-colors">
                      Marquer « Soumis »
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogue : modification d'une cible */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg" data-testid="backlink-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <Pencil className="w-5 h-5 text-gold" /> Modifier la cible
            </DialogTitle>
            <DialogDescription className="sr-only">Modification d'une cible de backlink</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <input type="text" value={editTarget.name} data-testid="backlink-edit-name"
                onChange={(e) => setEditTarget((t) => ({ ...t, name: e.target.value }))}
                className="w-full bg-ink border border-ivory/15 rounded-full px-4 py-2 text-sm text-ivory focus:border-gold/60 focus:outline-none" />
              <input type="text" value={editTarget.url || ""} placeholder="Adresse du site (optionnel)" data-testid="backlink-edit-url"
                onChange={(e) => setEditTarget((t) => ({ ...t, url: e.target.value }))}
                className="w-full bg-ink border border-ivory/15 rounded-full px-4 py-2 text-sm text-ivory focus:border-gold/60 focus:outline-none" />
              <input type="email" value={editTarget.email || ""} placeholder="Courriel du contact (pour l'envoi direct)" data-testid="backlink-edit-email"
                onChange={(e) => setEditTarget((t) => ({ ...t, email: e.target.value }))}
                className="w-full bg-ink border border-ivory/15 rounded-full px-4 py-2 text-sm text-ivory focus:border-gold/60 focus:outline-none" />
              <div className="flex gap-3">
                <Select value={editTarget.category} onValueChange={(v) => setEditTarget((t) => ({ ...t, category: v }))}>
                  <SelectTrigger data-testid="backlink-edit-category" className="flex-1 bg-ink border-ivory/15 rounded-full text-sm text-ivory">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-ivory/15 text-ivory">
                    {Object.entries(CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={editTarget.priority} onValueChange={(v) => setEditTarget((t) => ({ ...t, priority: v }))}>
                  <SelectTrigger data-testid="backlink-edit-priority" className="flex-1 bg-ink border-ivory/15 rounded-full text-sm text-ivory">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-ivory/15 text-ivory">
                    {Object.entries(PRIORITES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <textarea rows={4} value={editTarget.notes || ""} placeholder="Notes" data-testid="backlink-edit-notes"
                onChange={(e) => setEditTarget((t) => ({ ...t, notes: e.target.value }))}
                className="w-full bg-ink border border-ivory/15 rounded-xl px-4 py-3 text-sm text-ivory focus:border-gold/60 focus:outline-none" />
              <button data-testid="backlink-edit-save" onClick={saveEdit} disabled={editBusy}
                className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-2.5 rounded-full text-sm hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50">
                {editBusy ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} Enregistrer
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
